import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { getWorkflowByType } from '../config/workflowConfig';

function Warnings({ showToast }) {
  const navigate = useNavigate();
  const {
    warnings: allWarnings,
    customers: allCustomers,
    models,
    updateWarning,
    deleteWarning,
    addWarningHistory,
    runWarningForCustomer,
    runWarningForAll,
    getIndicatorById,
    addCase,
    cases
  } = useData();
  
  const { filterWarningsByBranch } = useAuth();

  // Filter warnings by user's accessible branches
  const warnings = filterWarningsByBranch(allWarnings, allCustomers);
  const customers = allCustomers; // Keep all customers for lookup purposes

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRiskLevels, setSelectedRiskLevels] = useState(['high']); // Show high risk by default
  const [selectedStatuses, setSelectedStatuses] = useState(['new', 'in_progress']);
  const [selectedCustomerTypes, setSelectedCustomerTypes] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState('warning');
  const [isRunningAll, setIsRunningAll] = useState(false);
  const [selectedModelForRun, setSelectedModelForRun] = useState(null);

  const getActiveCaseForWarning = (warningId) => {
    return cases.find(c => c.warningId === warningId && c.status !== 'closed' && c.isHidden !== true);
  };

  const getCaseStatusInfo = (status) => {
    const statusMap = {
      in_progress: { label: 'Đang xử lý', color: 'warning' },
      completed: { label: 'Hoàn thành', color: 'success' },
      closed: { label: 'Đã đóng', color: 'secondary' },
      new: { label: 'Mới tạo', color: 'primary' }
    };
    return statusMap[status] || statusMap.in_progress;
  };

  // Filtered warnings
  const filteredWarnings = warnings.filter(w => {
    const matchSearch = !searchTerm ||
      w.customerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      w.code.toLowerCase().includes(searchTerm.toLowerCase());

    const matchRisk = selectedRiskLevels.length === 0 || selectedRiskLevels.includes(w.riskLevel);
    const matchStatus = selectedStatuses.length === 0 || selectedStatuses.includes(w.status);
    const matchType = selectedCustomerTypes.length === 0 || selectedCustomerTypes.includes(w.customerType);

    return matchSearch && matchRisk && matchStatus && matchType;
  });

  // Statistics
  const stats = {
    total: warnings.length,
    high: warnings.filter(w => w.riskLevel === 'high').length,
    medium: warnings.filter(w => w.riskLevel === 'medium').length,
    low: warnings.filter(w => w.riskLevel === 'low').length,
    new: warnings.filter(w => w.status === 'new').length,
    inProgress: warnings.filter(w => w.status === 'in_progress').length,
    resolved: warnings.filter(w => w.status === 'resolved').length
  };

  // Get risk level info
  const getRiskLevelInfo = (level) => {
    const info = {
      high: { label: 'Cao', color: 'danger', bg: '#fee', icon: 'exclamation-triangle-fill' },
      medium: { label: 'Trung bình', color: 'warning', bg: '#fff3cd', icon: 'exclamation-circle-fill' },
      low: { label: 'Thấp', color: 'info', bg: '#cff4fc', icon: 'info-circle-fill' },
      none: { label: 'An toàn', color: 'success', bg: '#d1e7dd', icon: 'check-circle-fill' }
    };
    return info[level] || info.none;
  };

  // Get status info
  const getStatusInfo = (status) => {
    const info = {
      new: { label: 'Mới', color: 'primary', icon: 'bell-fill' },
      in_progress: { label: 'Đang xử lý', color: 'warning', icon: 'hourglass-split' },
      resolved: { label: 'Đã xử lý', color: 'success', icon: 'check-circle-fill' }
    };
    return info[status] || info.new;
  };
  
  // Create case from warning
  const handleCreateCase = (warning) => {
    // Check if case already exists for this warning
    const existingCase = cases.find(c => c.warningId === warning.id && c.status !== 'closed');
    
    if (existingCase) {
      showToast && showToast('Case đã tồn tại cho cảnh báo này!', 'warning');
      navigate('/case-management');
      return;
    }
    
    // Get customer info
    const customer = customers.find(c => c.id === warning.customerId);
    if (!customer) {
      showToast && showToast('Không tìm thấy thông tin khách hàng!', 'error');
      return;
    }
    
    // Create new case
    const newCase = {
      id: `CASE-${Date.now()}`,
      warningId: warning.id,
      warningCode: warning.code,
      warningLevel: warning.riskLevel,
      warningScore: warning.score,
      alertType: warning.alertType,
      customerId: customer.id,
      customerName: customer.name,
      customerType: warning.customerType,
      riskLevel: warning.riskLevel,
      currentWorkflow: 'cbs',
      currentStepId: 'cbs_4',
      status: 'in_progress',
      isManual: true, // Đánh dấu case được tạo manual
      isHidden: false, // Hiển thị case
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      history: [
        {
          stepId: 'cbs_1',
          action: 'select_customer',
          timestamp: new Date().toISOString(),
          user: 'System',
          notes: `Tạo case từ cảnh báo ${warning.code}`,
          status: 'completed'
        }
      ],
      data: {
        warningData: {
          score: warning.score,
          maxScore: warning.maxScore,
          thresholds: warning.thresholds,
          details: warning.details,
          model: warning.modelName
        }
      }
    };
    
    addCase(newCase);
    showToast && showToast('✅ Đã tạo Case thành công!', 'success');
    
    // Update warning status to in_progress
    updateWarning(warning.id, { status: 'in_progress' });
    
    // Navigate to case management
    navigate('/case-management');
  };

  // Handle run warning for single customer
  const handleRunSingle = (customerId, modelId, warningId = null) => {
    const warning = warningId ? warnings.find(w => w.id === warningId) : null;
    const resolvedCustomerId = warning?.customerId || customerId;
    const customer = customers.find(c => c.id === resolvedCustomerId || c.code === resolvedCustomerId);
    if (!customer) {
      showToast && showToast('Không tìm thấy khách hàng', 'error');
      return;
    }

    // Fallback sang model đang active đầu tiên nếu warning cũ không có modelId.
    const resolvedModelId = modelId || warning?.modelId || modelOptions[0]?.value;
    if (!resolvedModelId) {
      showToast && showToast('Không có mô hình active để chạy lại đánh giá', 'warning');
      return;
    }

    const result = runWarningForCustomer(resolvedCustomerId, resolvedModelId, 'Ngô Văn A', warningId);
    if (result) {
      showToast && showToast(`Đã chạy đánh giá cho ${customer.name}. Điểm: ${result.score}/100`, 'success');
    } else {
      showToast && showToast('Có lỗi khi chạy đánh giá', 'error');
    }
  };

  // Handle run warning for all customers
  const handleRunAll = () => {
    if (!selectedModelForRun) {
      showToast && showToast('Vui lòng chọn mô hình để chạy', 'warning');
      return;
    }

    setIsRunningAll(true);
    setTimeout(() => {
      const results = runWarningForAll(selectedModelForRun.value, 'Ngô Văn A');
      setIsRunningAll(false);
      showToast && showToast(`Đã chạy đánh giá cho ${results.length} khách hàng`, 'success');
      setSelectedModelForRun(null);
    }, 2000);
  };

  // Handle status change
  const handleStatusChange = (warning, newStatus, notes = '') => {
    const now = new Date().toISOString().slice(0, 16).replace('T', ' ');
    const updates = {
      status: newStatus,
      lastCheckedDate: now,
      lastCheckedBy: 'Ngô Văn A'
    };

    if (newStatus === 'resolved') {
      updates.resolvedDate = now;
      updates.resolvedBy = 'Ngô Văn A';
    } else if (newStatus === 'in_progress' && !warning.assignedTo) {
      updates.assignedTo = 'Ngô Văn A';
    }

    updateWarning(warning.id, updates);

    const statusLabels = {
      new: 'Tạo mới',
      in_progress: 'Bắt đầu xử lý',
      resolved: 'Hoàn thành xử lý'
    };

    addWarningHistory(warning.id, {
      date: now,
      score: warning.score,
      riskLevel: warning.riskLevel,
      status: newStatus,
      action: statusLabels[newStatus],
      user: 'Ngô Văn A',
      notes: notes || `Chuyển trạng thái sang ${getStatusInfo(newStatus).label}`
    });

    showToast(`Đã ${statusLabels[newStatus].toLowerCase()}`, 'success');
    setShowDetailModal(false);
  };

  // Open detail modal
  const openDetailModal = (warning) => {
    setSelectedWarning(warning);
    setActiveDetailTab('warning');
    setShowDetailModal(true);
  };

  const selectedWarningCase = selectedWarning ? getActiveCaseForWarning(selectedWarning.id) : null;

  const modelOptions = models
    .filter(m => m.status === 'active')
    .map(m => ({ value: m.id, label: m.name }));

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="page-header-sticky">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h2 className="mb-1">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Quản lý Cảnh báo sớm
            </h2>
            <p className="text-muted mb-0">Theo dõi và xử lý các cảnh báo rủi ro khách hàng</p>
          </div>
          <div>
            <div className="d-flex gap-2">
              <div style={{width: '300px'}}>
                <Select
                options={modelOptions}
                value={selectedModelForRun}
                onChange={setSelectedModelForRun}
                placeholder="Chọn mô hình để chạy..."
                isClearable
              />
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleRunAll}
              disabled={!selectedModelForRun || isRunningAll}
            >
              {isRunningAll ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Đang chạy...
                </>
              ) : (
                <>
                  <i className="bi bi-play-circle me-2"></i>
                  Chạy đánh giá tất cả
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      </div>
      
      {/* Enhanced Statistics */}
      <div className="row mb-4 stagger-animation">
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #6c757d'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Tổng cảnh báo</h6>
                  <h3 className="mb-0 fw-bold">{stats.total}</h3>
                </div>
                <div className="bg-secondary bg-opacity-10 rounded p-2">
                  <i className="bi bi-bell-fill text-secondary" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #dc3545'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Rủi ro cao</h6>
                  <h3 className="mb-0 fw-bold text-danger">{stats.high}</h3>
                </div>
                <div className="bg-danger bg-opacity-10 rounded p-2">
                  <i className="bi bi-exclamation-triangle-fill text-danger" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #ffc107'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Rủi ro TB</h6>
                  <h3 className="mb-0 fw-bold text-warning">{stats.medium}</h3>
                </div>
                <div className="bg-warning bg-opacity-10 rounded p-2">
                  <i className="bi bi-exclamation-circle-fill text-warning" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #0d6efd'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Cảnh báo mới</h6>
                  <h3 className="mb-0 fw-bold text-primary">{stats.new}</h3>
                </div>
                <div className="bg-primary bg-opacity-10 rounded p-2">
                  <i className="bi bi-bell-fill text-primary" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #ff9800'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Đang xử lý</h6>
                  <h3 className="mb-0 fw-bold" style={{color: '#ff9800'}}>{stats.inProgress}</h3>
                </div>
                <div className="rounded p-2" style={{backgroundColor: 'rgba(255, 152, 0, 0.1)'}}>
                  <i className="bi bi-hourglass-split" style={{fontSize: '1.5rem', color: '#ff9800'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-2 col-md-4 col-sm-6 mb-3">
          <div className="card border-0 shadow-sm h-100 hover-lift" style={{borderLeft: '4px solid #28a745'}}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 className="text-muted mb-1" style={{fontSize: '0.85rem'}}>Đã xử lý</h6>
                  <h3 className="mb-0 fw-bold text-success">{stats.resolved}</h3>
                </div>
                <div className="bg-success bg-opacity-10 rounded p-2">
                  <i className="bi bi-check-circle-fill text-success" style={{fontSize: '1.5rem'}}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            {/* Global Search */}
            <div className="col-lg-4">
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Tìm theo mã cảnh báo, mã KH, tên KH..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={() => setSearchTerm('')}
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Risk Level Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'high', label: '🔴 Cao' },
                  { value: 'medium', label: '🟡 Trung bình' },
                  { value: 'low', label: '🔵 Thấp' }
                ]}
                value={selectedRiskLevels.map(r => ({ value: r, label: r === 'high' ? '🔴 Cao' : r === 'medium' ? '🟡 Trung bình' : '🔵 Thấp' }))}
                onChange={(selected) => setSelectedRiskLevels(selected ? selected.map(s => s.value) : [])}
                placeholder="Mức độ rủi ro..."
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* Status Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'new', label: '🆕 Mới' },
                  { value: 'in_progress', label: '⏳ Đang xử lý' },
                  { value: 'resolved', label: '✅ Đã xử lý' }
                ]}
                value={selectedStatuses.map(s => ({ 
                  value: s, 
                  label: s === 'new' ? '🆕 Mới' : s === 'in_progress' ? '⏳ Đang xử lý' : '✅ Đã xử lý' 
                }))}
                onChange={(selected) => setSelectedStatuses(selected ? selected.map(s => s.value) : [])}
                placeholder="Trạng thái..."
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* Customer Type Filter */}
            <div className="col-lg-2">
              <Select
                isMulti
                options={[
                  { value: 'Khách hàng cá nhân', label: '👤 Cá nhân' },
                  { value: 'Khách hàng doanh nghiệp', label: '🏢 Doanh nghiệp' }
                ]}
                value={selectedCustomerTypes.map(t => ({ value: t, label: t }))}
                onChange={(selected) => setSelectedCustomerTypes(selected ? selected.map(s => s.value) : [])}
                placeholder="Loại KH..."
                isClearable
                styles={{
                  control: (base) => ({ ...base, minHeight: '38px' }),
                  multiValue: (base) => ({ ...base, backgroundColor: '#e7f3ff' })
                }}
              />
            </div>

            {/* View Mode Toggle */}
            <div className="col-lg-2">
              <div className="btn-group w-100" role="group">
                <button
                  className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('grid')}
                  title="Xem dạng lưới"
                >
                  <i className="bi bi-grid-3x3-gap"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('list')}
                  title="Xem dạng danh sách"
                >
                  <i className="bi bi-list-ul"></i>
                </button>
                <button
                  className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Xem dạng bảng"
                >
                  <i className="bi bi-table"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-3">
            <small className="text-muted">
              Hiển thị <strong>{filteredWarnings.length}</strong> / {warnings.length} cảnh báo
            </small>
          </div>
        </div>
      </div>

      {/* Warnings Display */}
      {filteredWarnings.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-inbox" style={{fontSize: '48px', opacity: 0.3}}></i>
            <p className="text-muted mt-3 mb-0">Không tìm thấy cảnh báo nào</p>
          </div>
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="row g-4 stagger-animation">
              {filteredWarnings.map(warning => {
                const riskInfo = getRiskLevelInfo(warning.riskLevel);
                const statusInfo = getStatusInfo(warning.status);
                const linkedCase = getActiveCaseForWarning(warning.id);
                return (
                  <div key={warning.id} className="col-lg-4 col-md-6">
                    <div 
                      className="card border-0 shadow-sm h-100 hover-lift" 
                      style={{borderLeft: `4px solid var(--bs-${riskInfo.color})`}}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <span className={`badge bg-${riskInfo.color} mb-2`}>
                              <i className={`bi bi-${riskInfo.icon} me-1`}></i>
                              {riskInfo.label}
                            </span>
                            <div className="small text-muted">{warning.code}</div>
                          </div>
                          <span className={`badge bg-${statusInfo.color}`}>
                            <i className={`bi bi-${statusInfo.icon} me-1`}></i>
                            {statusInfo.label}
                          </span>
                        </div>

                        <h5 className="card-title mb-2">{warning.customerName}</h5>
                        <div className="mb-3">
                          <small className="text-muted d-block">{warning.customerId}</small>
                          <small className="text-muted d-block">{warning.customerType}</small>
                        </div>

                        {/* Score */}
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="fw-bold">Điểm rủi ro:</small>
                            <span className={`badge bg-${riskInfo.color}`}>{warning.score}/{warning.maxScore}</span>
                          </div>
                          <div className="progress" style={{height: '8px'}}>
                            <div 
                              className={`progress-bar bg-${riskInfo.color}`}
                              style={{width: `${warning.score}%`}}
                            ></div>
                          </div>
                        </div>

                        {/* Alert Type */}
                        <div className="mb-3">
                          <small className="text-muted">Loại cảnh báo:</small>
                          <div className="fw-bold">{warning.alertType}</div>
                        </div>

                        {/* Timestamps */}
                        <div className="mb-3">
                          <small className="text-muted d-block">
                            <i className="bi bi-clock me-1"></i>
                            Tạo: {warning.createdDate}
                          </small>
                          <small className="text-muted d-block">
                            <i className="bi bi-arrow-repeat me-1"></i>
                            Cập nhật: {warning.lastCheckedDate}
                          </small>
                        </div>

                        {/* Actions */}
                        <div className="d-flex flex-column gap-2">
                          <div className="d-flex gap-2">
                            <button 
                              className="btn btn-sm btn-outline-primary flex-fill"
                              onClick={() => openDetailModal(warning)}
                              title="Xem chi tiết"
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                            <button 
                              className="btn btn-sm btn-outline-secondary flex-fill"
                              onClick={() => handleRunSingle(warning.customerId, warning.modelId, warning.id)}
                              title="Chạy lại"
                            >
                              <i className="bi bi-arrow-repeat"></i>
                            </button>
                          </div>
                          {linkedCase ? (
                            <button
                              className="btn btn-sm btn-outline-success w-100"
                              onClick={() => navigate('/case-management')}
                              title="Case đã tồn tại - mở màn quản lý Case"
                            >
                              <i className="bi bi-folder-check me-1"></i>
                              Đã có Case
                            </button>
                          ) : (
                            <button 
                              className="btn btn-sm btn-danger w-100"
                              onClick={() => handleCreateCase(warning)}
                              title="Tạo Case xử lý"
                            >
                              <i className="bi bi-folder-plus me-1"></i>
                              Tạo Case
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="row g-3 stagger-animation">
              {filteredWarnings.map(warning => {
                const riskInfo = getRiskLevelInfo(warning.riskLevel);
                const statusInfo = getStatusInfo(warning.status);
                const linkedCase = getActiveCaseForWarning(warning.id);
                return (
                  <div key={warning.id} className="col-12">
                    <div className="card border-0 shadow-sm hover-lift" style={{borderLeft: `4px solid var(--bs-${riskInfo.color})`}}>
                      <div className="card-body">
                        <div className="row align-items-center">
                          <div className="col-md-5">
                            <div className="d-flex align-items-start gap-3">
                              <div className={`badge bg-${riskInfo.color} align-self-start`}>
                                {warning.score}
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-1">{warning.customerName}</h6>
                                <small className="text-muted d-block">{warning.code} • {warning.customerId}</small>
                                <small className="text-muted d-block">{warning.alertType}</small>
                              </div>
                            </div>
                          </div>
                          <div className="col-md-3">
                            <div className="text-center">
                              <span className={`badge bg-${riskInfo.color} mb-1`}>
                                <i className={`bi bi-${riskInfo.icon} me-1`}></i>
                                {riskInfo.label}
                              </span>
                              <br/>
                              <span className={`badge bg-${statusInfo.color}`}>
                                <i className={`bi bi-${statusInfo.icon} me-1`}></i>
                                {statusInfo.label}
                              </span>
                            </div>
                          </div>
                          <div className="col-md-2">
                            <small className="text-muted d-block">Cập nhật:</small>
                            <small className="d-block">{warning.lastCheckedDate}</small>
                          </div>
                          <div className="col-md-2">
                            <div className="d-flex flex-column gap-1 justify-content-end">
                              <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-outline-primary" onClick={() => openDetailModal(warning)}>
                                  <i className="bi bi-eye"></i>
                                </button>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRunSingle(warning.customerId, warning.modelId, warning.id)}>
                                  <i className="bi bi-arrow-repeat"></i>
                                </button>
                              </div>
                              {linkedCase ? (
                                <button
                                  className="btn btn-sm btn-outline-success w-100"
                                  onClick={() => navigate('/case-management')}
                                  title="Case đã tồn tại - mở màn quản lý Case"
                                >
                                  <i className="bi bi-folder-check me-1"></i>
                                  Đã có Case
                                </button>
                              ) : (
                                <button 
                                  className="btn btn-sm btn-danger w-100" 
                                  onClick={() => handleCreateCase(warning)}
                                >
                                  <i className="bi bi-folder-plus me-1"></i>
                                  Tạo Case
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="card border-0 shadow-sm">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Mã CB</th>
                      <th>Khách hàng</th>
                      <th>Loại CB</th>
                      <th className="text-center">Điểm</th>
                      <th className="text-center">Mức độ</th>
                      <th className="text-center">Trạng thái</th>
                      <th>Ngày tạo</th>
                      <th className="text-center">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredWarnings.map(warning => {
                      const riskInfo = getRiskLevelInfo(warning.riskLevel);
                      const statusInfo = getStatusInfo(warning.status);
                      return (
                        <tr key={warning.id}>
                          <td><code>{warning.code}</code></td>
                          <td>
                            <strong>{warning.customerName}</strong>
                            <div className="small text-muted">{warning.customerId}</div>
                          </td>
                          <td><small>{warning.alertType}</small></td>
                          <td className="text-center">
                            <span className={`badge bg-${riskInfo.color}`}>{warning.score}</span>
                          </td>
                          <td className="text-center">
                            <span className={`badge bg-${riskInfo.color}`}>{riskInfo.label}</span>
                          </td>
                          <td className="text-center">
                            <span className={`badge bg-${statusInfo.color}`}>
                              <i className={`bi bi-${statusInfo.icon} me-1`}></i>
                              {statusInfo.label}
                            </span>
                          </td>
                          <td><small>{warning.createdDate}</small></td>
                          <td className="text-center">
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => openDetailModal(warning)}>
                                <i className="bi bi-eye"></i>
                              </button>
                              <button className="btn btn-outline-secondary" onClick={() => handleRunSingle(warning.customerId, warning.modelId, warning.id)}>
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                              {warning.status === 'new' && (
                                <button className="btn btn-outline-success" onClick={() => handleStatusChange(warning, 'in_progress')}>
                                  <i className="bi bi-play-circle"></i>
                                </button>
                              )}
                              {warning.status === 'in_progress' && (
                                <button className="btn btn-outline-success" onClick={() => handleStatusChange(warning, 'resolved')}>
                                  <i className="bi bi-check-circle"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal - Style giống ảnh 3 */}
      {showDetailModal && selectedWarning && (
        <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{maxWidth: '95%'}}>
            <div className="modal-content" style={{borderRadius: '12px', overflow: 'hidden'}}>
              {/* Header */}
              <div className="modal-header border-0" style={{backgroundColor: '#f8f9fa'}}>
                <h5 className="modal-title fw-bold">
                  Chi tiết cảnh báo - {selectedWarning.code}
                </h5>
                <button 
                  className="btn-close" 
                  onClick={() => setShowDetailModal(false)}
                ></button>
              </div>

              <div className="modal-body p-4">
                {(() => {
                  // Get full customer details
                  const customer = customers.find(c => c.id === selectedWarning.customerId);
                  const linkedCase = selectedWarningCase;
                  const workflow = linkedCase ? getWorkflowByType(linkedCase.currentWorkflow || 'cbs') : null;
                  const workflowSteps = workflow?.steps || [];
                  const completedStepCount = workflowSteps.filter(step =>
                    (linkedCase?.history || []).some(item => item.stepId === step.id)
                  ).length;
                  const totalStepCount = workflowSteps.length;
                  const progressPercent = totalStepCount > 0
                    ? Math.round((completedStepCount / totalStepCount) * 100)
                    : 0;
                  const currentStep = workflowSteps.find(step => step.id === linkedCase?.currentStepId);
                  const caseStatusInfo = linkedCase ? getCaseStatusInfo(linkedCase.status) : null;
                  
                  return (
                    <>
                      {linkedCase && (
                        <ul className="nav nav-tabs mb-4">
                          <li className="nav-item">
                            <button
                              className={`nav-link ${activeDetailTab === 'warning' ? 'active' : ''}`}
                              onClick={() => setActiveDetailTab('warning')}
                            >
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              Thông tin cảnh báo
                            </button>
                          </li>
                          <li className="nav-item">
                            <button
                              className={`nav-link ${activeDetailTab === 'case' ? 'active' : ''}`}
                              onClick={() => setActiveDetailTab('case')}
                            >
                              <i className="bi bi-diagram-3 me-1"></i>
                              Theo dõi Case ({linkedCase.id})
                            </button>
                          </li>
                        </ul>
                      )}

                      {(!linkedCase || activeDetailTab === 'warning') && (
                      <>
                      <div className="row g-4">
                        {/* Left Column - Thông tin khách hàng */}
                        <div className="col-md-6">
                          <h6 className="text-primary fw-bold mb-3">Thông tin khách hàng</h6>
                          
                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Mã Cảnh báo:</div>
                              <div className="col-7 fw-bold">{selectedWarning.code}</div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Mã KH:</div>
                              <div className="col-7 fw-bold">{customer?.code || selectedWarning.customerId}</div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Tên:</div>
                              <div className="col-7 fw-bold">{selectedWarning.customerName}</div>
                            </div>
                          </div>

                          {customer?.branch && (
                            <div className="mb-3">
                              <div className="row">
                                <div className="col-5 text-muted small">Chi nhánh:</div>
                                <div className="col-7">{customer.branch}</div>
                              </div>
                            </div>
                          )}

                          {customer?.businessLicense && (
                            <div className="mb-3">
                              <div className="row">
                                <div className="col-5 text-muted small">Mã số thuế:</div>
                                <div className="col-7">{customer.businessLicense}</div>
                              </div>
                            </div>
                          )}

                          {customer?.idNumber && (
                            <div className="mb-3">
                              <div className="row">
                                <div className="col-5 text-muted small">CMND/CCCD:</div>
                                <div className="col-7">{customer.idNumber}</div>
                              </div>
                            </div>
                          )}

                          {customer?.totalDebt && (
                            <div className="mb-3">
                              <div className="row">
                                <div className="col-5 text-muted small">Tổng nợ:</div>
                                <div className="col-7 fw-bold text-danger">
                                  {new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(customer.totalDebt)}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Loại cảnh báo:</div>
                              <div className="col-7">
                                <span className="badge bg-secondary">{selectedWarning.alertType}</span>
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Trạng thái:</div>
                              <div className="col-7">
                                <span className={`badge bg-${getStatusInfo(selectedWarning.status).color}`}>
                                  {getStatusInfo(selectedWarning.status).label}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div className="row">
                              <div className="col-5 text-muted small">Thời gian:</div>
                              <div className="col-7">{selectedWarning.createdDate}</div>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Đánh giá rủi ro */}
                        <div className="col-md-6">
                          <h6 className="text-primary fw-bold mb-3">Đánh giá rủi ro</h6>
                          
                          <div className="mb-3">
                            <div className="small text-muted mb-1">Điểm cảnh báo:</div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="fw-bold" style={{fontSize: '2rem', color: `var(--bs-${getRiskLevelInfo(selectedWarning.riskLevel).color})`}}>
                                {selectedWarning.score}/{selectedWarning.maxScore}
                              </span>
                              <span className={`badge bg-${getRiskLevelInfo(selectedWarning.riskLevel).color}`} style={{fontSize: '1rem', padding: '8px 12px'}}>
                                {selectedWarning.score}%
                              </span>
                            </div>
                            <div className="progress" style={{height: '12px', borderRadius: '6px'}}>
                              <div 
                                className={`progress-bar bg-${getRiskLevelInfo(selectedWarning.riskLevel).color}`}
                                style={{width: `${selectedWarning.score}%`}}
                              ></div>
                            </div>
                          </div>

                          <div className="p-3 rounded" style={{backgroundColor: getRiskLevelInfo(selectedWarning.riskLevel).bg}}>
                            <h5 className={`text-${getRiskLevelInfo(selectedWarning.riskLevel).color} fw-bold mb-0`}>
                              Mức độ: {getRiskLevelInfo(selectedWarning.riskLevel).label}
                            </h5>
                          </div>
                        </div>
                      </div>

                      {/* Khuyến nghị */}
                      <div className="mt-4 p-3 rounded" style={{backgroundColor: '#fff9e6'}}>
                        <div className="d-flex align-items-start">
                          <i className="bi bi-lightbulb-fill text-warning me-2" style={{fontSize: '1.2rem'}}></i>
                          <div className="flex-grow-1">
                            <h6 className="fw-bold mb-2">Khuyến nghị:</h6>
                            <ul className="mb-0">
                              {(selectedWarning.recommendations || []).map((rec, idx) => (
                                <li key={idx}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Indicator Details */}
                      <div className="mt-4">
                        <div className="d-flex align-items-center mb-3">
                          <i className="bi bi-list-check text-primary me-2" style={{fontSize: '1.3rem'}}></i>
                          <h6 className="fw-bold mb-0">Chi tiết các chỉ tiêu</h6>
                        </div>
                        <div className="card border-0 shadow-sm">
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-hover mb-0" style={{fontSize: '0.9rem'}}>
                                <thead style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6'}}>
                                  <tr>
                                    <th className="text-center py-3" style={{width: '4%', fontWeight: 600}}>STT</th>
                                    <th className="py-3" style={{width: '12%', fontWeight: 600}}>Phân loại chỉ tiêu</th>
                                    <th className="py-3" style={{width: '18%', fontWeight: 600}}>Chỉ tiêu cảnh báo sớm</th>
                                    <th className="text-center py-3" style={{width: '9%', fontWeight: 600}}>Trọng số</th>
                                    <th className="text-center py-3" style={{width: '10%', fontWeight: 600}}>Tần suất</th>
                                    <th className="text-end py-3" style={{width: '12%', fontWeight: 600}}>Điểm thực tế</th>
                                    <th className="text-center py-3" style={{width: '10%', fontWeight: 600}}>Điểm rủi ro</th>
                                    <th className="text-center py-3" style={{width: '12%', fontWeight: 600}}>Mức độ rủi ro</th>
                                    <th className="py-3" style={{width: '13%', fontWeight: 600}}>Ngày chấm</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(selectedWarning.indicators || []).map((ind, idx) => {
                                    const indRiskInfo = getRiskLevelInfo(ind.riskLevel);
                                    return (
                                      <tr key={idx} style={{borderBottom: '1px solid #f0f0f0'}}>
                                        <td className="text-center py-3">
                                          <span className="badge bg-secondary" style={{fontSize: '0.85rem'}}>{idx + 1}</span>
                                        </td>
                                        <td className="py-3">
                                          <span className="badge" style={{backgroundColor: '#e3f2fd', color: '#1976d2', fontSize: '0.85rem'}}>
                                            {ind.category}
                                          </span>
                                        </td>
                                        <td className="py-3" style={{fontWeight: 500}}>{ind.name}</td>
                                        <td className="text-center py-3">
                                          <span className="badge bg-info" style={{fontSize: '0.85rem'}}>{(ind.weight * 100).toFixed(0)}%</span>
                                        </td>
                                        <td className="text-center py-3" style={{color: '#666'}}>{ind.frequency || 'Hàng tháng'}</td>
                                        <td className="text-end py-3">
                                          <span className="fw-bold" style={{fontSize: '0.95rem', color: '#2c3e50'}}>
                                            {ind.actualValue} <span className="text-muted" style={{fontSize: '0.85rem'}}>{ind.unit}</span>
                                          </span>
                                        </td>
                                        <td className="text-center py-3">
                                          <span className="fw-bold" style={{fontSize: '0.95rem'}}>{ind.score}<span className="text-muted">/{ind.maxScore}</span></span>
                                        </td>
                                        <td className="text-center py-3">
                                          <span className={`badge bg-${indRiskInfo.color}`} style={{fontSize: '0.85rem', padding: '6px 12px'}}>
                                            {indRiskInfo.label}
                                          </span>
                                        </td>
                                        <td className="py-3" style={{color: '#666', fontSize: '0.9rem'}}>{ind.checkedDate}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CBS History */}
                      {selectedWarning.cbsHistory && selectedWarning.cbsHistory.length > 0 && (
                        <div className="mt-4">
                          <div className="d-flex align-items-center mb-3">
                            <i className="bi bi-graph-up-arrow text-primary me-2" style={{fontSize: '1.3rem'}}></i>
                            <h6 className="fw-bold mb-0" style={{color: '#0066cc'}}>KẾT QUẢ CBS</h6>
                          </div>
                          <div className="card border-0 shadow-sm">
                            <div className="card-body p-0">
                              <div className="table-responsive">
                                <table className="table table-hover mb-0" style={{fontSize: '0.9rem'}}>
                                  <thead style={{backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6'}}>
                                    <tr>
                                      <th className="text-center py-3" style={{width: '10%', fontWeight: 600}}>Lần chấm</th>
                                      <th className="text-center py-3" style={{width: '12%', fontWeight: 600}}>Tổng điểm</th>
                                      <th className="text-center py-3" style={{width: '14%', fontWeight: 600}}>Mức độ rủi ro</th>
                                      <th className="py-3" style={{width: '15%', fontWeight: 600}}>Người thực hiện</th>
                                      <th className="text-center py-3" style={{width: '12%', fontWeight: 600}}>Vai trò</th>
                                      <th className="py-3" style={{width: '15%', fontWeight: 600}}>Ngày chấm</th>
                                      <th className="py-3" style={{width: '22%', fontWeight: 600}}>Ghi chú</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {selectedWarning.cbsHistory.map((cbs, idx) => {
                                      const cbsRiskColor = cbs.riskLevel === 'Cao' ? 'danger' : 
                                                          cbs.riskLevel === 'Trung bình' ? 'warning' : 'success';
                                      return (
                                        <tr key={idx} style={{borderBottom: '1px solid #f0f0f0'}}>
                                          <td className="text-center py-3">
                                            <div style={{
                                              width: '36px',
                                              height: '36px',
                                              borderRadius: '50%',
                                              backgroundColor: '#6c757d',
                                              color: 'white',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontWeight: 600,
                                              fontSize: '0.9rem'
                                            }}>
                                              {cbs.round}
                                            </div>
                                          </td>
                                          <td className="text-center py-3">
                                            <span className="fw-bold" style={{fontSize: '1.3rem', color: '#2c3e50'}}>
                                              {cbs.score}
                                            </span>
                                          </td>
                                          <td className="text-center py-3">
                                            <span className={`badge bg-${cbsRiskColor}`} style={{fontSize: '0.9rem', padding: '8px 16px'}}>
                                              {cbs.riskLevel}
                                            </span>
                                          </td>
                                          <td className="py-3" style={{fontWeight: 500}}>{cbs.user}</td>
                                          <td className="text-center py-3">
                                            <span className="badge" style={{backgroundColor: '#00bcd4', color: 'white', fontSize: '0.85rem', padding: '6px 12px'}}>
                                              {cbs.role}
                                            </span>
                                          </td>
                                          <td className="py-3" style={{color: '#666', fontSize: '0.9rem'}}>{cbs.date}</td>
                                          <td className="py-3" style={{color: '#666', lineHeight: '1.5'}}>{cbs.notes}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* History */}
                      {selectedWarning.history && selectedWarning.history.length > 0 && (
                        <div className="mt-4">
                          <h6 className="fw-bold mb-3">Lịch sử xử lý:</h6>
                          <div className="timeline">
                              {(selectedWarning.history || []).slice(0, 3).map((hist, idx) => {
                              const histRiskInfo = getRiskLevelInfo(hist.riskLevel);
                              return (
                                <div key={hist.id} className="d-flex gap-3 mb-3">
                                  <div className="text-center" style={{minWidth: '50px'}}>
                                    <div className={`badge bg-${histRiskInfo.color} rounded-circle p-2`} style={{width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                      {hist.score}
                                    </div>
                                    {idx < selectedWarning.history.slice(0, 3).length - 1 && (
                                      <div style={{width: '2px', height: '30px', backgroundColor: '#dee2e6', margin: '4px auto'}}></div>
                                    )}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                      <strong>{hist.action}</strong>
                                      <small className="text-muted">{hist.date}</small>
                                    </div>
                                    <small className="text-muted d-block">{hist.user}</small>
                                    {hist.notes && <small className="d-block mt-1">{hist.notes}</small>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      </>
                      )}

                      {linkedCase && activeDetailTab === 'case' && (
                        <>
                          <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body">
                              <div className="row g-3 align-items-center">
                                <div className="col-md-4">
                                  <small className="text-muted d-block">Case ID</small>
                                  <strong>{linkedCase.id}</strong>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted d-block">Workflow</small>
                                  <span className="badge bg-primary">{workflow?.name || linkedCase.currentWorkflow}</span>
                                </div>
                                <div className="col-md-4">
                                  <small className="text-muted d-block">Trạng thái case</small>
                                  <span className={`badge bg-${caseStatusInfo?.color || 'secondary'}`}>{caseStatusInfo?.label || linkedCase.status}</span>
                                </div>
                              </div>
                              <div className="mt-3">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <small className="text-muted">Tiến độ step</small>
                                  <strong>{completedStepCount}/{totalStepCount} ({progressPercent}%)</strong>
                                </div>
                                <div className="progress" style={{height: '10px'}}>
                                  <div className="progress-bar bg-success" style={{width: `${progressPercent}%`}}></div>
                                </div>
                                {currentStep && (
                                  <small className="text-muted d-block mt-2">
                                    Step hiện tại: <strong>{currentStep.name}</strong> ({currentStep.id})
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="card border-0 shadow-sm mb-4">
                            <div className="card-header bg-white">
                              <h6 className="mb-0">Theo dõi các step workflow</h6>
                            </div>
                            <div className="card-body">
                              <div className="row g-2">
                                {workflowSteps.map(step => {
                                  const isCompleted = (linkedCase.history || []).some(item => item.stepId === step.id);
                                  const isCurrent = linkedCase.currentStepId === step.id;
                                  return (
                                    <div className="col-md-6" key={step.id}>
                                      <div className={`border rounded p-3 ${isCompleted ? 'border-success bg-success bg-opacity-10' : isCurrent ? 'border-primary bg-primary bg-opacity-10' : 'border-light'}`}>
                                        <div className="d-flex justify-content-between align-items-start">
                                          <div>
                                            <strong>{step.order}. {step.name}</strong>
                                            <div className="text-muted" style={{fontSize: '0.85rem'}}>{step.description}</div>
                                          </div>
                                          <span className={`badge bg-${isCompleted ? 'success' : isCurrent ? 'primary' : 'secondary'}`}>
                                            {isCompleted ? 'Hoàn thành' : isCurrent ? 'Đang thực hiện' : 'Chưa tới'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          <div className="card border-0 shadow-sm">
                            <div className="card-header bg-white d-flex justify-content-between align-items-center">
                              <h6 className="mb-0">Nhật ký case gần nhất</h6>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setShowDetailModal(false);
                                  navigate('/case-management');
                                }}
                              >
                                <i className="bi bi-box-arrow-up-right me-1"></i>
                                Mở Case Management
                              </button>
                            </div>
                            <div className="card-body">
                              {(linkedCase.history || []).length === 0 ? (
                                <small className="text-muted">Chưa có lịch sử case</small>
                              ) : (
                                <div className="table-responsive">
                                  <table className="table table-sm align-middle mb-0">
                                    <thead>
                                      <tr>
                                        <th>Step</th>
                                        <th>Hành động</th>
                                        <th>Người thực hiện</th>
                                        <th>Thời gian</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(linkedCase.history || []).slice().reverse().slice(0, 8).map((item, idx) => (
                                        <tr key={`${item.stepId || 'step'}-${idx}`}>
                                          <td><small>{item.stepId || '-'}</small></td>
                                          <td>{item.action || '-'}</td>
                                          <td><small>{item.user || '-'}</small></td>
                                          <td><small>{item.timestamp ? new Date(item.timestamp).toLocaleString('vi-VN') : '-'}</small></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Footer với actions */}
              <div className="modal-footer border-0" style={{backgroundColor: '#f8f9fa'}}>
                <button 
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDetailModal(false);
                    setActiveDetailTab('warning');
                  }}
                >
                  Đóng
                </button>
                {selectedWarning.status === 'new' && (
                  <button 
                    className="btn btn-success"
                    onClick={() => handleStatusChange(selectedWarning, 'in_progress', 'Bắt đầu xử lý cảnh báo')}
                  >
                    <i className="bi bi-play-circle-fill me-2"></i>
                    Bắt đầu xử lý
                  </button>
                )}
                {selectedWarning.status === 'in_progress' && (
                  <button 
                    className="btn btn-success"
                    onClick={() => handleStatusChange(selectedWarning, 'resolved', 'Hoàn thành xử lý cảnh báo')}
                  >
                    <i className="bi bi-check-circle-fill me-2"></i>
                    Hoàn thành
                  </button>
                )}
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    handleRunSingle(selectedWarning.customerId, selectedWarning.modelId, selectedWarning.id);
                    setShowDetailModal(false);
                  }}
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Chạy lại đánh giá
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Warnings;
